import { FormEvent, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowRight, Building2, CheckCircle2, Eye, EyeOff, LockKeyhole, Mail, PhoneCall, Sparkles, User } from 'lucide-react'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'

function getErrorMessage(error: unknown) {
  const response = (error as { response?: { data?: { error?: string; message?: string } } })?.response
  return response?.data?.error || response?.data?.message || 'Nao foi possivel criar a conta agora.'
}

export default function Register() {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [organizationName, setOrganizationName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setErrorMessage('')
    setLoading(true)
    try {
      const response = await apiService.register({
        name,
        organization_name: organizationName,
        email,
        password,
        billing_email: email
      })
      const token = response.data?.data?.session?.access_token
      if (token) {
        localStorage.setItem('auth_token', token)
        toast.success('Conta criada e sessao iniciada. Sua IA esta pronta!')
        navigate('/app')
        return
      }
      toast.success('Conta criada. Entre para continuar.')
      navigate('/login')
    } catch (error) {
      const msg = getErrorMessage(error)
      setErrorMessage(msg)
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="mx-auto grid min-h-screen max-w-6xl gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[1fr_1fr] lg:px-8">
        {/* Left panel — form */}
        <section className="flex items-center justify-center order-2 lg:order-1">
          <div className="w-full max-w-md">
            <div className="mb-6 flex items-center gap-3 lg:hidden">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg">
                <PhoneCall className="h-5 w-5" />
              </div>
              <span className="text-lg font-bold text-slate-900">AiCall<span className="text-cyan-600">.Odonto</span></span>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-gradient-to-r from-cyan-50 to-blue-50 px-3 py-1 text-xs font-semibold text-cyan-700 mb-3">
                  <Sparkles className="h-3.5 w-3.5" />
                  Ative em 5 minutos
                </div>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900">Criar sua clinica</h1>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  Preencha os dados abaixo e sua IA de voz estara pronta para atender em minutos.
                </p>
              </div>

              <form onSubmit={handleSubmit} noValidate>
                <div className="mt-6 space-y-4">
                  <label className="block">
                    <span className="text-sm font-semibold text-slate-700">Nome</span>
                    <div className="relative mt-2">
                      <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        className="input pl-9"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder="Seu nome completo"
                        autoComplete="name"
                        required
                      />
                    </div>
                  </label>

                  <label className="block">
                    <span className="text-sm font-semibold text-slate-700">Clinica</span>
                    <div className="relative mt-2">
                      <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        className="input pl-9"
                        value={organizationName}
                        onChange={(event) => setOrganizationName(event.target.value)}
                        placeholder="Nome da sua clinica"
                        autoComplete="organization"
                        required
                      />
                    </div>
                  </label>

                  <label className="block">
                    <span className="text-sm font-semibold text-slate-700">E-mail</span>
                    <div className="relative mt-2">
                      <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        className="input pl-9"
                        type="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        placeholder="voce@clinicadental.com"
                        autoComplete="email"
                        required
                      />
                    </div>
                  </label>

                  <label className="block">
                    <span className="text-sm font-semibold text-slate-700">Senha</span>
                    <div className="relative mt-2">
                      <LockKeyhole className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        className="input pl-9 pr-10"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        placeholder="minimo de 8 caracteres"
                        autoComplete="new-password"
                        required
                        minLength={8}
                      />
                      <button
                        type="button"
                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-700"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </label>
                </div>

                {errorMessage && (
                  <p className="mt-3 text-sm text-danger-600">{errorMessage}</p>
                )}

                <button type="submit" className="btn-primary mt-6 h-11 w-full" disabled={loading}>
                  {loading ? 'Criando sua IA...' : 'Criar conta e ativar IA'}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-slate-500">
                Ja tem conta?{' '}
                <Link to="/login" className="font-semibold text-cyan-600 hover:text-cyan-700">
                  Entrar
                </Link>
              </p>
            </div>

            {/* Trust badges */}
            <div className="mt-4 flex items-center justify-center gap-4 text-xs text-slate-400">
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-cyan-500" />
                Ativacao em 5 min
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-cyan-500" />
                Sem fidelidade
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-cyan-500" />
                Suporte incluso
              </span>
            </div>
          </div>
        </section>

        {/* Right panel — hero */}
        <section className="order-1 flex flex-col justify-between rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-950 p-8 text-white sm:p-10 lg:order-2 relative overflow-hidden">
          <div className="absolute -top-20 -left-20 h-[300px] w-[300px] rounded-full bg-cyan-500/10 blur-3xl" />

          <Link to="/" className="flex items-center gap-3 relative z-10">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/25">
              <PhoneCall className="h-5 w-5" />
            </span>
            <span className="text-xl font-bold">AiCall<span className="text-cyan-400">.Odonto</span></span>
          </Link>

          <div className="my-12 relative z-10">
            <h2 className="text-4xl font-extrabold tracking-tight sm:text-5xl leading-tight">
              Sua IA de voz{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">pronta</span>
              {' '}em 5 minutos.
            </h2>
            <p className="mt-6 text-base leading-7 text-slate-300">
              Apos o cadastro, sua IA ja atende chamadas, dispara ligacoes em lote para sua base e faz follow-up automatico.
              Nao precisa instalar nada.
            </p>

            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-cyan-500/20 text-cyan-400 mt-0.5">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Atende e liga 24h</p>
                  <p className="text-xs text-slate-400 mt-0.5">Recepcao inteligente e disparo em lote para leads.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-cyan-500/20 text-cyan-400 mt-0.5">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Confirmacao e follow-up</p>
                  <p className="text-xs text-slate-400 mt-0.5">Ligacoes automaticas para confirmar consultas e pos-atendimento.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-cyan-500/20 text-cyan-400 mt-0.5">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Pipeline e dashboard</p>
                  <p className="text-xs text-slate-400 mt-0.5">Acompanhe chamadas, pacientes e resultados em tempo real.</p>
                </div>
              </div>
            </div>
          </div>

          <p className="relative z-10 text-xs font-medium text-slate-500">Sem periodo de teste. Assine o plano que preferir.</p>
        </section>
      </div>
    </main>
  )
}
